import {
    ButtonHTMLAttributes,
    ChangeEvent,
    ReactNode,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { IconType } from 'react-icons';
import {
    LuFile,
    LuFileAudio,
    LuFileImage,
    LuFileText,
    LuFileVideo,
    LuPlus,
    LuTrash2,
    LuUpload,
    LuX,
} from 'react-icons/lu';
import { MdError } from 'react-icons/md';
import GenericModal from '../ui/GenericModal';
import { cn } from '../../utilities/cn';
import { getUploadErrorMessage } from '../../api/upload';

type FileStatus = 'pending' | 'uploading' | 'success' | 'partial' | 'error';

export const DEFAULT_ALLOWED_FILE_TYPES = ['csv', 'xls', 'xlsx'];

/** 10 MB default upload limit. */
export const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const MAX_FILENAME_LENGTH = 255;

/** Brief delay so the completed upload state is visible before the modal closes. */
const MODAL_CLOSE_DELAY_MS = 500;
/**
 * After the file transfer completes (100%), wait briefly for a fast validation
 * error before closing the modal. Long-running ISIN processing continues in the
 * background while the table shows a pending state.
 */
const PROCESSING_CLOSE_DELAY_MS = 2000;
/** Upload transfer fills this much of the bar; the rest animates during the grace window. */
const TRANSFER_PROGRESS_MAX = 85;
const EMPTY_FILE_MESSAGE = 'Empty files cannot be uploaded.';
const INVALID_FILENAME_MESSAGE = 'Unable to use this file name.';

const EXTENSION_MIME_TYPES: Record<string, string[]> = {
    csv: [
        'text/csv',
        'application/csv',
        'text/comma-separated-values',
        'application/vnd.ms-excel',
    ],
    xls: ['application/vnd.ms-excel', 'application/vnd.ms-office'],
    xlsx: [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    txt: ['text/plain'],
    pdf: ['application/pdf'],
    doc: ['application/msword'],
    docx: [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    png: ['image/png'],
};

const normalizeExtension = (extension: string) =>
    extension.replace(/^\./, '').toLowerCase();

const getFileExtension = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex === -1) {
        return null;
    }
    return fileName.slice(dotIndex + 1).toLowerCase();
};

const getBasename = (fileName: string) => {
    const normalized = fileName.replace(/\\/g, '/');
    return normalized.slice(normalized.lastIndexOf('/') + 1);
};

const sanitizeFileName = (fileName: string) => {
    let sanitized = getBasename(fileName)
        .replace(/\0/g, '')
        .replace(/[\x00-\x1f\x7f]/g, '')
        .replace(/[/\\]/g, '')
        .trim();

    if (sanitized === '.' || sanitized === '..') {
        return '';
    }

    if (sanitized.length > MAX_FILENAME_LENGTH) {
        const extension = getFileExtension(sanitized);
        if (extension) {
            const baseName = sanitized.slice(0, sanitized.lastIndexOf('.'));
            const maxBaseLength = MAX_FILENAME_LENGTH - extension.length - 1;
            sanitized = `${baseName.slice(0, Math.max(maxBaseLength, 1))}.${extension}`;
        } else {
            sanitized = sanitized.slice(0, MAX_FILENAME_LENGTH);
        }
    }

    return sanitized;
};

const withSanitizedFileName = (file: File): File | null => {
    const sanitizedName = sanitizeFileName(file.name);
    if (!sanitizedName) {
        return null;
    }

    if (sanitizedName === file.name) {
        return file;
    }

    return new File([file], sanitizedName, {
        type: file.type,
        lastModified: file.lastModified,
    });
};

const buildFileTypeConfig = (allowedFileTypes: string[]) => {
    const extensions = allowedFileTypes.map(normalizeExtension);
    const labels = extensions.map((extension) => extension.toUpperCase());
    const mimeTypes = [
        ...new Set(
            extensions.flatMap(
                (extension) => EXTENSION_MIME_TYPES[extension] ?? [],
            ),
        ),
    ];
    const accept = [
        ...extensions.map((extension) => `.${extension}`),
        ...mimeTypes,
    ].join(',');

    return {
        accept,
        formatLabels: labels.join(', '),
        invalidMessage: `Only ${labels.join(', ')} files are allowed.`,
        isAllowedFile: (file: File) => {
            const extension = getFileExtension(file.name);
            if (!extension || !extensions.includes(extension)) {
                return false;
            }

            const allowedMimeTypes = EXTENSION_MIME_TYPES[extension];
            if (!allowedMimeTypes || file.type === '') {
                return true;
            }

            return allowedMimeTypes.includes(file.type);
        },
    };
};

const getMaxFileSizeError = (maxFileSizeBytes: number) =>
    `File exceeds the ${formatFileSize(maxFileSizeBytes)} size limit.`;

const validateSelectedFile = (
    file: File,
    fileTypeConfig: ReturnType<typeof buildFileTypeConfig>,
    maxFileSizeBytes: number,
): string | null => {
    if (file.size === 0) {
        return EMPTY_FILE_MESSAGE;
    }

    if (file.size > maxFileSizeBytes) {
        return getMaxFileSizeError(maxFileSizeBytes);
    }

    if (!fileTypeConfig.isAllowedFile(file)) {
        return fileTypeConfig.invalidMessage;
    }

    return null;
};

type FileWithProgress = {
    id: string;
    file: File;
    progress: number;
    displayProgress: number;
    status: FileStatus;
    errorMessage?: string;
};

const getTransferDisplayProgress = (progress: number) =>
    Math.round(Math.min(progress, 100) * (TRANSFER_PROGRESS_MAX / 100));

/** Describes the spreadsheet layout an uploaded file is expected to follow. */
export type ExpectedFileShape = {
    /** Column header(s) shown in the first row. */
    columns: string[];
    /** Example data rows (values align with `columns`). */
    exampleRows?: string[][];
    /** Shorthand for single-column files: one example value per row. */
    exampleValues?: string[];
    /** Caller-provided explanation of the expected format. */
    description?: ReactNode;
    note?: ReactNode;
};

export type FileUploadEventContext = {
    uploadId: string;
    fileName: string;
};

type FileUploadProps<TData = unknown> = {
    allowedFileTypes?: string[];
    maxFileSizeBytes?: number;
    title?: string;
    /** Performs the upload and resolves with the response data. */
    onUpload: (params: {
        file: File;
        onProgress: (progress: number) => void;
    }) => Promise<TData>;
    /** Return an error message when the response should be treated as a failed upload. */
    validateResult?: (data: TData) => string | null;
    /** Maps a successful response to the modal completion status. Defaults to `success`. */
    getUploadStatus?: (data: TData) => 'success' | 'partial';
    /**
     * Optional custom UI shown inside the modal describing the expected file.
     * Keep upload-format specifics in the caller and pass them here.
     */
    expectedFilePreview?: ReactNode;
    /** Fired when an upload begins processing on the server. */
    onUploadStarted?: (context: FileUploadEventContext) => void;
    /** Fired when the upload resolves with a valid result. */
    onUploadSuccess?: (
        context: FileUploadEventContext & { data: TData },
    ) => void;
    /** Fired when the upload request fails. */
    onUploadError?: (
        context: FileUploadEventContext & { message: string },
    ) => void;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick' | 'title' | 'type'>;

const createUploadId = () =>
    `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function FileUpload<TData = unknown>({
    allowedFileTypes = DEFAULT_ALLOWED_FILE_TYPES,
    maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
    title = 'Upload File',
    onUpload,
    validateResult,
    getUploadStatus,
    expectedFilePreview,
    onUploadStarted,
    onUploadSuccess,
    onUploadError,
    className,
    ...buttonProps
}: FileUploadProps<TData>) {
    const fileTypeConfig = useMemo(
        () => buildFileTypeConfig(allowedFileTypes),
        [allowedFileTypes],
    );
    const fileInputId = useId();

    const [isOpen, setIsOpen] = useState(false);
    const [file, setFile] = useState<FileWithProgress | null>(null);
    const [selectionError, setSelectionError] = useState<string | null>(null);
    const [activeUploadCount, setActiveUploadCount] = useState(0);

    const inputRef = useRef<HTMLInputElement>(null);
    const closeTimeoutRef = useRef<number | null>(null);
    const processingCloseTimeoutRef = useRef<number | null>(null);
    const modalClosedForProcessingRef = useRef(false);
    const modalUploadIdRef = useRef<string | null>(null);
    const graceAnimationFrameRef = useRef<number | null>(null);
    const graceAnimationUploadIdRef = useRef<string | null>(null);

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current !== null) {
                window.clearTimeout(closeTimeoutRef.current);
            }
            if (processingCloseTimeoutRef.current !== null) {
                window.clearTimeout(processingCloseTimeoutRef.current);
            }
            if (graceAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(graceAnimationFrameRef.current);
            }
        };
    }, []);

    const cancelProgressAnimation = () => {
        graceAnimationUploadIdRef.current = null;

        if (graceAnimationFrameRef.current !== null) {
            window.cancelAnimationFrame(graceAnimationFrameRef.current);
            graceAnimationFrameRef.current = null;
        }
    };

    /**
     * Drives the progress bar on a timer from upload start, so it keeps filling
     * even when the browser does not emit granular upload-progress events (common
     * for small files). Real transfer progress, when available, raises a floor via
     * `onProgress`. Eases toward 95% and is finalised to 100% on success.
     */
    const startProgressAnimation = (uploadId: string) => {
        cancelProgressAnimation();
        graceAnimationUploadIdRef.current = uploadId;

        const startedAt = performance.now();

        const animate = (now: number) => {
            if (graceAnimationUploadIdRef.current !== uploadId) {
                return;
            }

            const elapsed = now - startedAt;
            const t = Math.min(elapsed / PROCESSING_CLOSE_DELAY_MS, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            const value = eased * 95;

            setFile((prev) =>
                prev?.id === uploadId
                    ? {
                        ...prev,
                        displayProgress: Math.max(prev.displayProgress, value),
                    }
                    : prev,
            );

            if (t < 1) {
                graceAnimationFrameRef.current = window.requestAnimationFrame(animate);
            } else {
                graceAnimationFrameRef.current = null;
            }
        };

        graceAnimationFrameRef.current = window.requestAnimationFrame(animate);
    };

    const cancelProcessingClose = () => {
        cancelProgressAnimation();

        if (processingCloseTimeoutRef.current !== null) {
            window.clearTimeout(processingCloseTimeoutRef.current);
            processingCloseTimeoutRef.current = null;
        }
    };

    const scheduleCloseForProcessing = () => {
        if (processingCloseTimeoutRef.current !== null) {
            return;
        }

        processingCloseTimeoutRef.current = window.setTimeout(() => {
            processingCloseTimeoutRef.current = null;
            modalClosedForProcessingRef.current = true;
            setFile(null);
            setSelectionError(null);
            setIsOpen(false);
        }, PROCESSING_CLOSE_DELAY_MS);
    };

    const showUploadError = (
        uploadId: string,
        fileName: string,
        fileToUpload: File,
        errorMessage: string,
    ) => {
        onUploadError?.({ uploadId, fileName, message: errorMessage });

        if (modalUploadIdRef.current === uploadId) {
            if (modalClosedForProcessingRef.current) {
                modalClosedForProcessingRef.current = false;
                setIsOpen(true);
                setFile({
                    id: uploadId,
                    file: fileToUpload,
                    progress: 100,
                    displayProgress: 100,
                    status: 'error',
                    errorMessage,
                });
                return;
            }

            setFile((prev) =>
                prev?.id === uploadId
                    ? { ...prev, status: 'error', errorMessage }
                    : prev,
            );
        }
    };

    function handleOpen() {
        cancelProcessingClose();
        modalClosedForProcessingRef.current = false;
        setFile(null);
        setSelectionError(null);
        setIsOpen(true);
    }

    function handleClose(open: boolean) {
        const modalUploadInProgress = file?.status === 'uploading';

        if (!open && modalUploadInProgress) {
            return;
        }

        setIsOpen(open);

        if (!open) {
            setFile(null);
            setSelectionError(null);
        }
    }

    function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
        const selected = e.target.files?.[0];
        if (!selected) {
            return;
        }

        const sanitizedFile = withSanitizedFileName(selected);
        if (!sanitizedFile) {
            setSelectionError(INVALID_FILENAME_MESSAGE);
            setFile(null);
        } else {
            const validationError = validateSelectedFile(
                sanitizedFile,
                fileTypeConfig,
                maxFileSizeBytes,
            );

            if (validationError) {
                setSelectionError(validationError);
                setFile(null);
            } else {
                setSelectionError(null);
                setFile({
                    file: sanitizedFile,
                    progress: 0,
                    displayProgress: 0,
                    status: 'pending',
                    id: sanitizedFile.name,
                });
            }
        }

        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }

    async function handleUpload() {
        if (!file || file.status === 'success' || file.status === 'uploading') {
            return;
        }

        const validationError = validateSelectedFile(
            file.file,
            fileTypeConfig,
            maxFileSizeBytes,
        );

        if (validationError) {
            setSelectionError(validationError);
            return;
        }

        const uploadId = createUploadId();
        const fileName = file.file.name;
        const fileToUpload = file.file;

        modalClosedForProcessingRef.current = false;
        cancelProcessingClose();
        modalUploadIdRef.current = uploadId;

        setFile((prev) =>
            prev
                ? {
                    ...prev,
                    id: uploadId,
                    progress: 0,
                    displayProgress: 0,
                    status: 'uploading',
                    errorMessage: undefined,
                }
                : prev,
        );
        setActiveUploadCount((count) => count + 1);
        onUploadStarted?.({ uploadId, fileName });

        // Drive the bar and the auto-close on a timer from the start. Browsers
        // often skip upload-progress events for small files, so we cannot rely on
        // `onProgress` reaching 100% to begin either.
        startProgressAnimation(uploadId);
        scheduleCloseForProcessing();

        const closeAfterDelay = () => {
            if (closeTimeoutRef.current !== null) {
                window.clearTimeout(closeTimeoutRef.current);
            }

            closeTimeoutRef.current = window.setTimeout(() => {
                closeTimeoutRef.current = null;
                modalClosedForProcessingRef.current = false;
                if (modalUploadIdRef.current === uploadId) {
                    modalUploadIdRef.current = null;
                }
                setFile(null);
                setSelectionError(null);
                setIsOpen(false);
            }, MODAL_CLOSE_DELAY_MS);
        };

        try {
            const data = await onUpload({
                file: fileToUpload,
                onProgress: (progress) => {
                    if (modalUploadIdRef.current !== uploadId) {
                        return;
                    }

                    // Raise the bar to the real transfer position if it is ahead
                    // of the timed animation; never move it backwards.
                    const transferFloor = getTransferDisplayProgress(progress);

                    setFile((prev) =>
                        prev?.id === uploadId
                            ? {
                                ...prev,
                                progress,
                                displayProgress: Math.max(
                                    prev.displayProgress,
                                    transferFloor,
                                ),
                            }
                            : prev,
                    );
                },
            });

            cancelProcessingClose();

            const resultError = validateResult?.(data) ?? null;
            if (resultError) {
                showUploadError(
                    uploadId,
                    fileName,
                    fileToUpload,
                    resultError,
                );
                return;
            }

            const wasClosedForProcessing = modalClosedForProcessingRef.current;

            if (modalUploadIdRef.current === uploadId && !wasClosedForProcessing) {
                const status = getUploadStatus?.(data) ?? 'success';
                setFile((prev) =>
                    prev?.id === uploadId
                        ? { ...prev, progress: 100, displayProgress: 100, status }
                        : prev,
                );
                closeAfterDelay();
            }

            onUploadSuccess?.({ uploadId, fileName, data });
        } catch (error) {
            cancelProcessingClose();
            showUploadError(
                uploadId,
                fileName,
                fileToUpload,
                getUploadErrorMessage(error),
            );
        } finally {
            setActiveUploadCount((count) => Math.max(0, count - 1));
            if (modalUploadIdRef.current === uploadId) {
                modalUploadIdRef.current = null;
            }
        }
    }

    function handleClear() {
        setFile(null);
        setSelectionError(null);
    }

    const modalUploadInProgress = file?.status === 'uploading';
    const backgroundUploadCount = Math.max(
        0,
        activeUploadCount - (modalUploadInProgress ? 1 : 0),
    );

    const canUpload =
        file
        && (file.status === 'pending' || file.status === 'error')
        && !modalUploadInProgress;

    const displayError =
        selectionError
        ?? (file?.status === 'error' ? file.errorMessage : null);

    return (
        <>
            <button
                type="button"
                onClick={handleOpen}
                className={cn(
                    'flex items-center gap-1.5 rounded-md bg-grayscale-700 px-3 py-1.5 text-sm hover:opacity-90',
                    className,
                )}
                {...buttonProps}
            >
                <LuUpload size={14} />
                {title}
            </button>

            <GenericModal
                isOpen={isOpen}
                setIsOpen={handleClose}
                title={title}
                preventClose={modalUploadInProgress}
                className="max-w-xl"
            >
                <div className="flex flex-col gap-2">
                    {backgroundUploadCount > 0 ? (
                        <p className="text-xs text-mainblue">
                            {backgroundUploadCount} file upload
                            {backgroundUploadCount === 1 ? '' : 's'} processing
                            in the background. You can start another below.
                        </p>
                    ) : null}
                    <p className="text-xs text-gray-600">
                        Allowed formats: {fileTypeConfig.formatLabels}. Max size:{' '}
                        {formatFileSize(maxFileSizeBytes)}.
                    </p>
                    {expectedFilePreview ?? null}
                    {displayError ? (
                        <div className="flex items-start gap-1 text-xs text-red-600">
                            <MdError className="mt-0.5 shrink-0" size={14} />
                            <span>{displayError}</span>
                        </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            type="file"
                            ref={inputRef}
                            onChange={handleFileSelect}
                            accept={fileTypeConfig.accept}
                            className="hidden"
                            id={fileInputId}
                            disabled={modalUploadInProgress}
                        />
                        <label
                            htmlFor={fileInputId}
                            className="flex cursor-pointer items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-200"
                        >
                            <LuPlus size={14} />
                            Select File
                        </label>
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={!canUpload}
                            className="flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <LuUpload size={14} />
                            {file?.status === 'error' ? 'Retry' : 'Upload'}
                        </button>
                        <button
                            type="button"
                            onClick={handleClear}
                            disabled={!file || modalUploadInProgress}
                            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <LuTrash2 size={14} />
                            Clear
                        </button>
                    </div>

                    {file ? (
                        <FileItem
                            file={file}
                            onRemove={handleClear}
                            uploading={modalUploadInProgress}
                        />
                    ) : null}
                </div>
            </GenericModal>
        </>
    );
}

const COLUMN_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const getColumnLetter = (index: number) => {
    let letter = '';
    let current = index;
    do {
        letter = COLUMN_LETTERS[current % 26] + letter;
        current = Math.floor(current / 26) - 1;
    } while (current >= 0);
    return letter;
};

const isIsinColumn = (column: string) => /^isin$/i.test(column);

export function ExpectedFileShapePreview({ shape }: { shape: ExpectedFileShape }) {
    const dataRows =
        shape.exampleRows ??
        shape.exampleValues?.map((value) => [value]) ??
        [shape.columns.map(() => '…')];

    const gutterCell =
        'border border-gray-300 bg-gray-100 px-1 py-0.5 text-center text-[10px] font-medium text-gray-500';
    const letterCell = cn(gutterCell, 'bg-gray-200');
    const dataCell =
        'border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-900 whitespace-nowrap';

    return (
        <div className="rounded border border-gray-200 bg-gray-50 p-2">
            <p className="text-xs font-semibold text-gray-800">
                Expected file format
            </p>
            {shape.description ? (
                <p className="mt-0.5 text-xs leading-snug text-gray-500">
                    {shape.description}
                </p>
            ) : null}

            <div className="mt-1.5 max-w-full overflow-x-auto">
                <table className="border-separate border-spacing-0">
                    <tbody>
                        <tr>
                            <td className={cn(letterCell, 'w-7')} />
                            {shape.columns.map((column, colIndex) => (
                                <td
                                    key={`letter-${column}`}
                                    className={cn(
                                        letterCell,
                                        isIsinColumn(column)
                                            ? 'min-w-[7.5rem]'
                                            : 'min-w-[11rem]',
                                    )}
                                >
                                    {getColumnLetter(colIndex)}
                                </td>
                            ))}
                        </tr>

                        <tr>
                            <td className={cn(gutterCell, 'w-7')}>1</td>
                            {shape.columns.map((column) => (
                                <td
                                    key={`header-${column}`}
                                    className={cn(
                                        dataCell,
                                        'bg-mainblue font-semibold text-white',
                                        isIsinColumn(column) ? 'font-mono' : '',
                                    )}
                                >
                                    {column}
                                </td>
                            ))}
                        </tr>

                        {dataRows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className={cn(gutterCell, 'w-7')}>
                                    {rowIndex + 2}
                                </td>
                                {shape.columns.map((column, colIndex) => (
                                    <td
                                        key={`${rowIndex}-${column}`}
                                        className={cn(
                                            dataCell,
                                            isIsinColumn(column)
                                                ? 'font-mono'
                                                : 'font-normal uppercase tracking-tight',
                                        )}
                                    >
                                        {row[colIndex] ?? '…'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {shape.note ? (
                <p className="mt-1.5 text-xs text-gray-500">{shape.note}</p>
            ) : null}
        </div>
    );
}


type FileItemProps = {
    file: FileWithProgress;
    onRemove: () => void;
    uploading: boolean;
};

function FileItem({ file, onRemove, uploading }: FileItemProps) {
    const Icon = getFileIcon(file.file.type);
    const showProgress =
        file.status === 'uploading'
        || file.status === 'success'
        || file.status === 'partial';

    return (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="flex items-center gap-2">
                <Icon size={18} className="shrink-0 text-mainblue" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-gray-900">
                            {file.file.name}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500">
                            {formatFileSize(file.file.size)}
                        </span>
                        <StatusBadge
                            status={file.status}
                            displayProgress={file.displayProgress}
                            processing={
                                file.status === 'uploading'
                                && file.displayProgress >= TRANSFER_PROGRESS_MAX
                            }
                        />
                    </div>
                    {showProgress ? (
                        <ProgressBar
                            progress={file.displayProgress}
                            status={file.status}
                            animating={file.status === 'uploading'}
                        />
                    ) : null}
                </div>
                {!uploading ? (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="shrink-0 p-0.5 hover:opacity-80"
                        aria-label="Remove file"
                    >
                        <LuX size={14} className="text-grayscale-400" />
                    </button>
                ) : null}
            </div>
        </div>
    );
}

type StatusBadgeProps = {
    status: FileStatus;
    displayProgress: number;
    processing?: boolean;
};

function StatusBadge({
    status,
    displayProgress,
    processing = false,
}: StatusBadgeProps) {
    if (status === 'pending') {
        return null;
    }

    const labels: Record<Exclude<FileStatus, 'pending'>, string> = {
        uploading: processing
            ? 'Processing…'
            : `${Math.round(displayProgress)}%`,
        success: 'Done',
        partial: 'Partial',
        error: 'Failed',
    };

    const colors: Record<Exclude<FileStatus, 'pending'>, string> = {
        uploading: 'text-gray-500',
        success: 'text-green-600',
        partial: 'text-amber-600',
        error: 'text-red-600',
    };

    return (
        <span className={cn('shrink-0 text-xs', colors[status])}>
            {labels[status]}
        </span>
    );
}

type ProgressBarProps = {
    progress: number;
    status: FileStatus;
    /** While uploading, width is driven frame-by-frame, so skip the CSS transition. */
    animating?: boolean;
};

function ProgressBar({ progress, status, animating = false }: ProgressBarProps) {
    const barColor =
        status === 'success'
            ? 'bg-green-500'
            : status === 'partial'
                ? 'bg-amber-500'
                : status === 'error'
                    ? 'bg-red-500'
                    : 'bg-mainblue';

    return (
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-200">
            <div
                className={cn(
                    'h-full',
                    barColor,
                    animating ? '' : 'transition-all duration-300',
                )}
                style={{ width: `${Math.max(progress, 2)}%` }}
            />
        </div>
    );
}

const getFileIcon = (mimeType: string): IconType => {
    if (mimeType.startsWith('image/')) return LuFileImage;
    if (mimeType.startsWith('video/')) return LuFileVideo;
    if (mimeType.startsWith('audio/')) return LuFileAudio;
    if (mimeType === 'application/pdf') return LuFileText;
    return LuFile;
};

const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};
